<template>
    <div class="notification-bell" @click="toggleNotifications">
         <el-tooltip class="box-item" effect="dark" content="Guía de configuración" placement="bottom-end">
            <MapPinned :size="24" class="has-text-light"  @click="viewSetupGuide()"/>
        </el-tooltip>

        <el-drawer v-model="drawer_setup_guide" class="drawer-setup-guide" size="600px">
            <template #header>
                <div class="drawer-header">
                    <h3>Guía de configuración inicial</h3>
                    <p class="setup-subtitle">Completa estos pasos para configurar tu clínica</p>
                </div>
            </template>
            <template #default>
                <div class="setup-content" v-loading="loading_full">
                    <!-- Progress Overview -->
                    <el-row :gutter="20" class="progress-section">
                        <el-col :span="24">
                            <el-card class="progress-card" shadow="never">
                                <div class="progress-header">
                                    <h4>Progreso general</h4>
                                    <span class="progress-percentage">{{ progressPercentage }}%</span>
                                </div>
                                <el-progress 
                                    :percentage="progressPercentage" 
                                    :stroke-width="12"
                                    :show-text="false"
                                    class="main-progress"
                                />
                                <div class="progress-stats">
                                    <span class="stats-item">
                                        <font-awesome-icon :icon="['fas', 'check']" />
                                        {{ completedSteps }} completados
                                    </span>
                                    <span class="stats-item">
                                        <font-awesome-icon :icon="['fas', 'clock']" />
                                        {{ pendingSteps }} pendientes
                                    </span>
                                </div>
                            </el-card>
                        </el-col>
                    </el-row>

                    <!-- Setup Steps -->
                    <el-row :gutter="16" class="steps-section">
                        <el-col :span="24" v-for="(item, index) in filteredSetupSteps" :key="item.id">
                            <el-card 
                                class="step-card" 
                                :class="getStepCardClass(item.status)"
                                shadow="hover"
                            >
                                <div class="step-content">
                                    <div class="step-icon-container">
                                        <font-awesome-icon 
                                            class="step-icon" 
                                            :class="getStepIconClass(item.status)"
                                            :icon="getStepIcon(item.status, item.system_setup_guide?.icon)"
                                            size="lg"
                                        />
                                    </div>
                                    
                                    <div class="step-details">
                                        <div class="step-header-row">
                                            <h5 class="step-title">
                                                <span class="step-number">Paso {{ item.step }}</span>
                                                {{ item.system_setup_guide?.module }}
                                            </h5>
                                            <el-tag 
                                                :type="getStatusTagType(item.status)"
                                                size="small"
                                                class="status-tag"
                                            >
                                                {{ getStatusText(item.status) }}
                                            </el-tag>
                                        </div>
                                        
                                        <p class="step-description">
                                            {{ item.system_setup_guide?.description }}
                                        </p>
                                        
                                        <div class="step-actions" v-if="item.status !== 'completado'">
                                            <el-button 
                                                :type="item.status === 'en_proceso' ? 'primary' : 'default'"
                                                size="small"
                                                @click="startStep(item)"
                                                :loading="item.actionLoading"
                                            >
                                                <font-awesome-icon :icon="['fas', 'arrow-right']" />&nbsp;
                                                {{ item.status === 'en_proceso' ? 'Continuar' : 'Comenzar' }}
                                            </el-button>
                                        </div>
                                    </div>
                                </div>
                            </el-card>
                        </el-col>
                    </el-row>

                    <!-- Completion Message -->
                    <el-row v-if="isCompleteSetup" class="completion-section">
                        <el-col :span="24">
                            <el-card class="completion-card" shadow="never">
                                <div class="completion-content">
                                    <font-awesome-icon class="completion-icon" :icon="['fas', 'trophy']" size="3x" />
                                    <h3>¡Configuración completada!</h3>
                                    <p>Has completado todos los pasos de configuración inicial. Tu clínica está lista para usar.</p>
                                    <el-button type="primary" @click="closeSetupGuide">
                                        Comenzar a usar la plataforma
                                    </el-button>
                                </div>
                            </el-card>
                        </el-col>
                    </el-row>
                </div>
            </template>
        </el-drawer>

    </div>

</template>
<script>

import apiClient from '@/api/apiClient';
import { showNotification } from '@/utils/notification';
import { useVeterinariaStore } from '@/stores/veterinaria';
import { useMenuStore } from '@/stores/menu';
import { effectScope } from 'vue';
import { MapPinned } from 'lucide-vue-next'



export default {
    name: "userSetupGuide",
    components: {
       MapPinned 
    },
    data() {
        return {
            toggleNotifications: false,
            drawer_setup_guide: false,
            loading_full: false,
            veterinariaStore: useVeterinariaStore(),
            menuStore: useMenuStore(),
            type_menu: useMenuStore().type_menu,
            setupSteps: [],
            progressPercentage: 0,
            completedSteps: 0,
            pendingSteps: 0,
            isCompleteSetup: false
        }
    },
    mounted() {

    },

    created() {
        
    },
    methods: {

        viewSetupGuide(){
            this.drawer_setup_guide = true;
            this.loadSetupProgress();
        },

        async loadSetupProgress() {
            try {
                this.loading_full = true;
                const response = await apiClient.get('/settings/system-user-setup-progress',
                    { params: { sucursal_id: this.sucursal_id } }
                );
                
                if (response.data.ok) {
                    this.setupSteps = response.data.data;
                    this.calculateProgress();
                }
            } catch (error) {
                console.error('Error loading setup progress:', error);
                showNotification('Error al cargar el progreso de configuración', 'error');
            } finally {
                this.loading_full = false;
            }
        },

        calculateProgress() {
            const totalSteps = this.filteredSetupSteps.length;
            const completed = this.filteredSetupSteps.filter(step => step.status === 'completado').length;
            
            this.completedSteps = completed;
            this.pendingSteps = totalSteps - completed;
            this.progressPercentage = totalSteps > 0 ? Math.round((completed / totalSteps) * 100) : 0;
            this.isCompleteSetup = this.progressPercentage === 100;
        },

        getStepCardClass(status) {
            return {
                'step-card-completed': status === 'completado',
                'step-card-in-process': status === 'en_proceso',
                'step-card-pending': status === 'pendiente'
            };
        },

        getStepIconClass(status) {
            return {
                'step-icon-completed': status === 'completado',
                'step-icon-in-process': status === 'en_proceso', 
                'step-icon-pending': status === 'pendiente'
            };
        },

        getStepIcon(status, moduleIcon) {
            // Map of icon corrections for Font Awesome
            const iconMap = {
                'desktop': 'computer',
                'clipboard-list': 'clipboard-list',
                'house': 'house',
                'home': 'house'
            };

            switch(status) {
                case 'completado':
                    // Use the specific module icon even when completed
                    if (moduleIcon) {
                        // Handle different icon formats from API
                        if (moduleIcon.includes('fa-')) {
                            // Format: "fa-solid fa-desktop" -> ['fas', 'computer']
                            const parts = moduleIcon.split(' ');
                            const style = parts[0] === 'fa-solid' ? 'fas' : parts[0] === 'fa-regular' ? 'far' : 'fas';
                            const originalIconName = parts[1]?.replace('fa-', '') || 'question';
                            const iconName = iconMap[originalIconName] || originalIconName;
                            return [style, iconName];
                        } else {
                            // Format: "house" -> ['fas', 'house']
                            const iconName = iconMap[moduleIcon] || moduleIcon;
                            return ['fas', iconName];
                        }
                    }
                    return ['fas', 'check-circle']; // fallback if no module icon
                case 'en_proceso':
                    return ['fas', 'spinner'];
                default:
                    // Use the specific module icon if available, otherwise use default
                    if (moduleIcon) {
                        // Handle different icon formats from API
                        if (moduleIcon.includes('fa-')) {
                            // Format: "fa-solid fa-desktop" -> ['fas', 'computer']
                            const parts = moduleIcon.split(' ');
                            const style = parts[0] === 'fa-solid' ? 'fas' : parts[0] === 'fa-regular' ? 'far' : 'fas';
                            const originalIconName = parts[1]?.replace('fa-', '') || 'question';
                            const iconName = iconMap[originalIconName] || originalIconName;
                            return [style, iconName];
                        } else {
                            // Format: "house" -> ['fas', 'house']
                            const iconName = iconMap[moduleIcon] || moduleIcon;
                            return ['fas', iconName];
                        }
                    }
                    return ['fas', 'exclamation-triangle'];
            }
        },

        getStatusTagType(status) {
            switch(status) {
                case 'completado':
                    return 'success';
                case 'en_proceso':
                    return 'warning';
                default:
                    return 'info';
            }
        },

        getStatusText(status) {
            switch(status) {
                case 'completado':
                    return 'Completado';
                case 'en_proceso':
                    return 'En proceso';
                default:
                    return 'Pendiente';
            }
        },

        async startStep(step) {
            // Aquí puedes redirigir al usuario a la página específica del paso
            // o mostrar un modal con instrucciones
            
            if (step.system_setup_guide?.link) {
                // Si hay un link específico, navegar a esa ruta
                this.drawer_setup_guide = false; // Cerrar el drawer antes de navegar
                this.$router.push(step.system_setup_guide.link);
            } else {
                this.drawer_setup_guide = false; // Cerrar el drawer antes de navegar
                // Mostrar información adicional o redirigir según el módulo
                this.handleStepNavigation(step);
            }
        },

        handleStepNavigation(step) {
            const module = step.system_setup_guide?.module?.toLowerCase();
            
            switch(module) {
                case 'agregar clínica':
                    // Redirigir a configuración de clínica
                    this.$router.push('/settings/veterinaria');
                    break;
                case 'servicios':
                    // Redirigir a gestión de servicios  
                    this.$router.push('/services');
                    break;
                default:
                    showNotification('Funcionalidad en desarrollo', 'info');
            }
            
            this.drawer_setup_guide = false;
        },

        closeSetupGuide() {
            this.drawer_setup_guide = false;
            showNotification('¡Configuración completada exitosamente!', '', 'success');
        }

    },
    computed: {
        sucursal_id() {
            return this.veterinariaStore.getMainVeterinaria()?.id ?? null;
        },
        filteredSetupSteps() {
            if (this.type_menu === 'admin') {
                const allowedModules = ['Ingreso al sistema', 'Agregar/Editar clínica'];
                return this.setupSteps.filter(item => allowedModules.includes(item.system_setup_guide?.module));
            }
            return this.setupSteps;
        }
    },
    watch: {
        'menuStore.type_menu'(newVal) {
            console.log('menuStore.type_menu changed:', newVal);
            this.type_menu = newVal;
            this.calculateProgress();
        }
    },


}
</script>

  
<style scoped>
/* Drawer Styling */
.drawer-setup-guide :deep(.el-drawer__header) {
    margin-bottom: 10px !important;
    padding: 24px 20px 16px 20px;
    border-bottom: 1px solid var(--el-border-color-lighter);
    background: linear-gradient(135deg, var(--color-primary) 0%, #0866a3 100%);
    color: white;
    margin: 0;
}

.drawer-setup-guide :deep(.el-drawer__body) {
    padding: 20px !important;
    background-color: #f8f9fa;
}

.drawer-header h3 {
    margin: 0 0 4px 0;
    font-size: 20px;
    font-weight: 600;
}

.setup-subtitle {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
}

/* Progress Section */
.progress-section {
    margin-bottom: 20px;
}

.progress-card {
    border: none;
    box-shadow: 0 2px 8px rgba(6, 120, 183, 0.1);
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.progress-header h4 {
    margin: 0;
    color: var(--bulma-content-heading-color);
    font-size: 16px;
    font-weight: 600;
}

.progress-percentage {
    font-size: 24px;
    font-weight: 700;
    color: var(--color-primary);
}

.main-progress :deep(.el-progress-bar__outer) {
    background-color: var(--color-primary-light);
}

.main-progress :deep(.el-progress-bar__inner) {
    background: linear-gradient(90deg, var(--color-primary) 0%, #0866a3 100%);
}

.progress-stats {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
}

.stats-item {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-info);
    font-size: 14px;
}

.stats-item svg {
    color: var(--color-primary);
}

/* Steps Section */
.steps-section {
    margin-bottom: 20px;
}

.step-card {
    margin-bottom: 12px;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.step-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--el-border-color-lighter);
    transition: background-color 0.3s ease;
}

.step-card-completed {
    border-color: var(--color-success-light);
    background: #f6ffed;
}

.step-card-completed::before {
    background: var(--color-success);
}

.step-card-in-process {
    border-color: var(--color-warning-light);
    background: #fffbf0;
    animation: pulse 2s infinite;
}

.step-card-in-process::before {
    background: var(--color-warning);
}

.step-card-pending:hover {
    border-color: var(--color-primary-light);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(6, 120, 183, 0.15);
}

@keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(230, 162, 60, 0.3); }
    70% { box-shadow: 0 0 0 8px rgba(230, 162, 60, 0); }
    100% { box-shadow: 0 0 0 0 rgba(230, 162, 60, 0); }
}

.step-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.step-icon-container {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--el-border-color-lighter);
    transition: all 0.3s ease;
}

.step-icon-completed {
    background: var(--color-success);
    border-color: var(--color-success);
    color: white;
}

.step-icon-in-process {
    background: var(--color-warning);
    border-color: var(--color-warning);
    color: white;
}

.step-icon-in-process svg {
    animation: rotate 1s linear infinite;
}

.step-icon-pending {
    border-color: var(--el-border-color-lighter);
    color: var(--color-info);
}

@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.step-details {
    flex: 1;
    min-width: 0;
}

.step-header-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
    gap: 12px;
}

.step-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--bulma-content-heading-color);
    line-height: 1.3;
}

.step-number {
    display: inline-block;
    background: var(--color-primary);
    color: white;
    font-size: 12px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 12px;
    margin-right: 8px;
}

.status-tag {
    flex-shrink: 0;
}

.step-description {
    margin: 0 0 16px 0;
    color: var(--color-info);
    line-height: 1.5;
    font-size: 14px;
}

.step-actions {
    display: flex;
    gap: 8px;
}

/* Completion Section */
.completion-section {
    margin-top: 24px;
}

.completion-card {
    border: 2px solid var(--color-success-light);
    background: linear-gradient(135deg, #f6ffed 0%, #f0fff4 100%);
}

.completion-content {
    text-align: center;
    padding: 24px;
}

.completion-icon {
    color: var(--color-warning);
    margin-bottom: 16px;
}

.completion-content h3 {
    margin: 0 0 12px 0;
    color: var(--color-success-dark);
    font-size: 20px;
    font-weight: 600;
}

.completion-content p {
    margin: 0 0 20px 0;
    color: var(--color-info);
    line-height: 1.5;
}

/* Badge for notification bell */
.badge {
    position: absolute;
    top: -1px;
    left: 29px;
    background: red;
    color: white;
    font-size: 12px;
    border-radius: 50%;
    padding: 0px 4px;
    min-width: 10px;
    text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
    .drawer-setup-guide :deep(.el-drawer) {
        width: 100% !important;
    }
    
    .step-content {
        gap: 12px;
    }
    
    .step-icon-container {
        width: 40px;
        height: 40px;
    }
    
    .step-header-row {
        flex-direction: column;
        gap: 8px;
    }
    
    .progress-stats {
        flex-direction: column;
        gap: 8px;
    }
}
</style>  